/* eslint-disable func-names */
// noinspection LocalVariableNamingConventionJS,FunctionWithMultipleLoopsJS

/*
|--------------------------------------------------------------------------
| LoadedRelationships
|--------------------------------------------------------------------------
|
| This plugin will allow you to track the relationships that have been loaded
| on a model instance. This can be useful when you need to know which
| relationships have been loaded on a model instance. This plugin borrows
| implementation inspiration from VuexORM's ChangeFlags plugin.
|
| https://github.com/vuex-orm/plugin-change-flags/blob/master/src/index.js
|
*/

const defaultOptions = {
    loadedRelationshipsFlagName: '$loadedRelationships',
    exposeLoadedRelationshipsExternally: true,
};

const relationshipTypes = [
    'BelongsTo',
    'HasOne',
    'HasMany',
    'HasManyBy',
    'BelongsToMany',
    'HasManyThrough',
    'MorphTo',
    'MorphOne',
    'MorphMany',
    'MorphToMany',
];

export default {
    install(components, installOptions) {
        const pluginOptions = {
            ...defaultOptions,
            ...installOptions,
        };

        const { Model, Query } = components;

        // Expose loaded relationships externally if enabled
        if (pluginOptions.exposeLoadedRelationshipsExternally) {
            // Modify the $fields method to include the loaded relationships flag
            const originalFieldsMethod = Model.prototype.$fields;

            Model.prototype.$fields = function () {
                return {
                    ...originalFieldsMethod.call(this),
                    [pluginOptions.loadedRelationshipsFlagName]: Model.attr([]),
                };
            };
        }

        /*
        |--------------------------------------------------------------------------
        | Query.with Override
        |--------------------------------------------------------------------------
        |
        | This will override the Query.with method to track the loaded relationships
        |
        */
        const originalWith = Query.prototype.with;

        /**
         * Override Query.with method to track the loaded relationships
         * @param {string} name
         * @param {function(*, null=): Query} constraint
         * @returns {Query}
         */
        Query.prototype.with = function (name, constraint = null) {
            this.trackLoadedRelationship(name);

            return originalWith.call(this, name, constraint);
        };

        /*
        |--------------------------------------------------------------------------
        | Query.withAll Override
        |--------------------------------------------------------------------------
        |
        | This will override the Query.withAll method to track the loaded
        | relationships that are being loaded dynamically.
        |
        */

        const originalWithAll = Query.prototype.withAll;

        /**
         * Override Query.withAll method to track the loaded relationships
         * @param {function: Query} constraint
         * @returns {Query}
         */
        Query.prototype.withAll = function (constraint = null) {
            const fields = this.model.fields();

            // Track all relationships defined on the model
            const relationships = Object.keys(fields)
                .map(field => {
                    const attribute = fields[field];

                    return relationshipTypes.includes(attribute.constructor.name) ? field : null;
                })
                .filter(Boolean);

            if (relationships.length) {
                this.trackLoadedRelationship(relationships);
            }

            return originalWithAll.call(this, constraint);
        };

        /*
        |--------------------------------------------------------------------------
        | Query.withAllRecursive Override
        |--------------------------------------------------------------------------
        |
        | This will override the Query.withAllRecursive method to track the loaded
        | relationships that are being loaded dynamically. Nested relationships
        | will also be tracked using dot notation.
        |
        */

        const originalWithAllRecursive = Query.prototype.withAllRecursive;

        /**
         * Override Query.withAll method to track the loaded relationships
         * @param {int} depth
         * @returns {Query}
         */
        Query.prototype.withAllRecursive = function (depth = 3) {
            const relationships = this.getAllRecursiveRelationships(depth);

            this.trackLoadedRelationship(relationships);
            return originalWithAllRecursive.call(this, depth);
        };

        Query.prototype.getAllRecursiveRelationships = function (depth = 3) {
            if (depth === 0) return [];

            const fields = this.model.fields();
            let relationships = [];

            // Loop through fields to find relationships
            Object.keys(fields).forEach(field => {
                const attribute = fields[field];

                // If the field is a relationship, add it to the list
                if (relationshipTypes.includes(attribute.constructor.name)) {
                    relationships.push(field);

                    // Recursively get relationships of the related model if available
                    if (depth > 1 && attribute.model) {
                        const relatedQuery = new Query(this.store, attribute.model.entity);

                        // Recursion
                        const relatedRelationships = relatedQuery.getAllRecursiveRelationships(depth - 1);

                        relationships = relationships.concat(relatedRelationships.map(rel => `${field}.${rel}`));
                    }
                }
            });

            return relationships;
        };

        // Track loaded relationships on the query instance
        Query.prototype.trackLoadedRelationship = function (relationship) {
            if (!this.loadedRelationships) {
                this.loadedRelationships = [];
            }

            // If it's an array of relationships, add each one
            if (Array.isArray(relationship)) {
                relationship.forEach(rel => {
                    if (!this.loadedRelationships.includes(rel)) {
                        this.loadedRelationships.push(rel);
                    }
                });
            } else if (!this.loadedRelationships.includes(relationship)) {
                this.loadedRelationships.push(relationship);
            }
        };

        // Transfer loaded relationships to model instances during hydration
        const originalHydrate = Query.prototype.hydrate;

        Query.prototype.hydrate = function (record) {
            const model = originalHydrate.call(this, record);

            model[pluginOptions.loadedRelationshipsFlagName] = this.loadedRelationships || [];

            // Propagate loaded relationships to child models
            Object.keys(this.model.fields()).forEach(field => {
                const attribute = this.model.fields()[field];

                if (relationshipTypes.includes(attribute.constructor.name) && model[field]) {
                    const relatedRecords = Array.isArray(model[field]) ? model[field] : [model[field]];

                    relatedRecords.forEach(relatedModel => {
                        relatedModel[pluginOptions.loadedRelationshipsFlagName] =
                            model[pluginOptions.loadedRelationshipsFlagName];
                    });
                }
            });

            return model;
        };

        Model.prototype.isRelationshipLoaded = function (relation) {
            const loadedRelationships = this.getLoadedRelationships();

            return loadedRelationships.some(loadedRel => {
                // Check if the relation is a part of any loaded relationship
                return loadedRel.startsWith(relation);
            });
        };

        // Add a method on the model to return the loaded relationships
        Model.prototype.getLoadedRelationships = function () {
            return this[pluginOptions.loadedRelationshipsFlagName] || [];
        };
    },
};
